import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  ShieldCheck,
  Zap,
  Activity,
  ChevronRight,
  Lock,
  Award,
  TrendingUp,
  Calendar,
  ArrowRight,
  ClipboardList,
  CalendarCheck,
  BadgeCheck,
  Megaphone,
} from "lucide-react";
import AthleteCard from "@/components/AthleteCard";
import SeenByMarquee from "@/components/SeenByMarquee";
import LazyImage from "@/components/LazyImage";

export default function Landing() {
  const [stats, setStats] = useState({
    verified_athletes: 0,
    total_athletes: 0,
    upcoming_events: 0,
    showcase: [],
  });
  const [events, setEvents] = useState([]);

  useEffect(() => {
    http.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
    http.get("/events").then(({ data }) => setEvents(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="bg-[#050505] text-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/15779126/pexels-photo-15779126.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#050505]" />
        <div className="absolute inset-0 grid-bg opacity-30" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-36">
          {(() => {
            const liveEvt = events.find((e) => e?.is_live);
            return liveEvt;
          })() ? (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-electric/40 bg-electric/10 backdrop-blur-md mb-8 animate-fade-up" data-testid="live-event-pill">
              <span className="w-1.5 h-1.5 rounded-full bg-electric animate-pulse" />
              <span className="label-mono text-electric">
                {(events.find((e) => e?.is_live) || {}).title ||
                 (events.find((e) => e?.is_live) || {}).name ||
                 "VERIFIED Combine"} · Live
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md mb-8 animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
              <span className="label-mono text-white/60">Trusted by parents · coaches · recruiters</span>
            </div>
          )}

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] font-black uppercase tracking-[0.01em] leading-[0.78] animate-fade-up stagger-1">
            Trust the<br />
            <span className="text-electric">VERIFIED</span><br />
            athlete.
          </h1>

          <p className="mt-8 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed animate-fade-up stagger-2">
            NFL-level athlete intelligence brought to high school sports. Independently verified
            measurables. Independently verified testing. Independent professional evaluations.
          </p>

          <div className="mt-10 flex flex-wrap gap-3 animate-fade-up stagger-3">
            <Link to="/register" className="btn-primary" data-testid="hero-get-verified-btn">
              Get VERIFIED <ChevronRight size={16} />
            </Link>
            <Link to="/dashboard/recruiter" className="btn-ghost" data-testid="hero-recruiter-btn">
              Recruiter Intel <ArrowRight size={14} />
            </Link>
          </div>

          {/* Stat strip */}
          <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-up stagger-4">
            <StatTile label="Verified Athletes" comingSoon />
            <StatTile label="Upcoming Events" comingSoon />
            <StatTile label="Published Reports" comingSoon />
            <StatTile label="Average 40 (V)" comingSoon />
          </div>
        </div>
      </section>

      {/* VALUE PROP BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <div className="label-mono mb-3">What VERIFIED Means</div>
            <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight max-w-2xl">
              Data you can <span className="text-electric">trust</span>.<br />
              Reports that mean something.
            </h2>
          </div>
          <p className="text-white/60 md:max-w-md text-sm">
            Every measurable on VERIFIED is captured and certified by the VERIFIED Team. Athletes
            can't self-edit verified fields. Recruiters get the truth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <BentoCard
            className="md:col-span-7 md:row-span-2 min-h-[280px]"
            icon={<Lock size={20} />}
            eyebrow="01 — Verified Data"
            title="Locked. Certified. Trusted."
            description="Once the VERIFIED Team captures and locks an athlete's height, weight, 40-yard, shuttle, vertical, broad jump and 3-cone — those numbers cannot be edited by athletes or parents. Recruiters see independently sourced truth."
            image="https://images.unsplash.com/photo-1763639700615-225fe7fdffff"
          />
          <BentoCard
            className="md:col-span-5"
            icon={<Activity size={20} />}
            eyebrow="02 — VERIFIED Testing"
            title="Full combine battery"
            description="40-yard, shuttle, 3-cone, vertical, broad jump — all run on calibrated equipment by the VERIFIED Team."
          />
          <BentoCard
            className="md:col-span-5"
            icon={<Award size={20} />}
            eyebrow="03 — Evaluation Engine"
            title="NFL-style scouting reports"
            description="Positional trait grading on a 1.0–9.0 scale. Projection. Scheme fit. Development roadmap."
          />
          <BentoCard
            className="md:col-span-4"
            icon={<TrendingUp size={20} />}
            eyebrow="04 — Recruiter Intel"
            title="Filter the truth"
            description="Search verified athletes by class, state, position, height, weight, 40-time, shuttle."
          />
          <BentoCard
            className="md:col-span-4"
            icon={<ShieldCheck size={20} />}
            eyebrow="05 — Parent Portal"
            title="Own your story"
            description="Manage your athlete's profile, upload media, track verification, register for events."
          />
          <BentoCard
            className="md:col-span-4"
            icon={<Zap size={20} />}
            eyebrow="06 — Mobile-first"
            title="Built for the sideline"
            description="Premium mobile experience. Scout, share, register from anywhere."
          />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 scroll-mt-20" data-testid="how-it-works">
        <div className="mb-12 max-w-2xl">
          <div className="label-mono mb-3">How It Works</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
            From sign-up to <span className="text-electric">scouted</span>.
          </h2>
          <p className="text-white/50 mt-4 text-sm">
            Four steps. No hype, no rankings — just verified data and a public profile recruiters can trust.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Step n="01" icon={<ClipboardList size={18} />} title="Create Your Profile" body="Register as a parent, add your athlete's basics — name, class, position, hometown, birthdate." />
          <Step n="02" icon={<CalendarCheck size={18} />} title="Book Your Test" body="Pick an open slot on the calendar. $199 flat — verified measurables, full testing battery, profile setup, badge." />
          <Step n="03" icon={<BadgeCheck size={18} />} title="Get VERIFIED" body="The VERIFIED Team captures height, weight, 40-yard, shuttle, vertical, broad and more — locked into your athlete's profile." />
          <Step n="04" icon={<Megaphone size={18} />} title="Get Discovered" body="Share your athlete's profile with one tap. Recruiters from D1 through D3, NAIA and JUCO can filter VERIFIED athletes by your athlete's measurables." />
        </div>
      </section>

      {/* MEET THE FOUNDER STRIP — trust anchor before social proof */}
      <section className="relative overflow-hidden border-y border-white/5 bg-[#070707]" data-testid="founder-strip">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="absolute -left-32 top-1/2 -translate-y-1/2 w-[420px] h-[420px] bg-electric/10 blur-[140px] rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-12 gap-10 items-center">
          {/* Photo */}
          <Link to="/about" className="md:col-span-4 group block" data-testid="founder-strip-photo">
            <div className="relative">
              <div className="absolute -inset-1 bg-electric/25 blur-2xl rounded-2xl opacity-70 group-hover:opacity-100 transition-opacity" />
              <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a]">
                <img
                  src="/founder/brandon-taylor.jpeg"
                  alt="Brandon Taylor — Founder, VERIFIED"
                  className="w-full h-auto object-cover aspect-[4/5] group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 via-black/30 to-transparent">
                  <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] font-bold uppercase text-electric border border-electric/40 px-2 py-1 bg-black/50">
                    <span className="w-1.5 h-1.5 rounded-full bg-electric animate-pulse" />
                    Founder
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {/* Copy */}
          <div className="md:col-span-8">
            <div className="text-xs tracking-[0.3em] font-bold uppercase text-electric mb-3">
              Built By An NFL Scout · Not A Recruiting Service
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight leading-[1.04] text-white">
              20+ Years In The League.
              <br />
              <span className="text-electric">One Mission: Real Information.</span>
            </h2>
            <p className="mt-5 text-base text-white/70 leading-relaxed max-w-2xl">
              VERIFIED was founded by <strong className="text-white">Brandon Taylor</strong> — a
              two-decade NFL scout who's worked the Carolina Panthers, Tennessee Titans, Columbus
              Destroyers, and the NFL Combine in Indianapolis through National Football Scouting.
              He built this platform so families finally have access to the same caliber of evaluation,
              data, and context that the league holds itself to.
            </p>

            {/* Logos row — small, monochrome */}
            <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-4 opacity-80">
              <img src="/founder/carolina-panthers.png" alt="Carolina Panthers" className="h-9 md:h-10 object-contain" loading="lazy" />
              <img src="/founder/tennessee-titans.png" alt="Tennessee Titans" className="h-9 md:h-10 object-contain" loading="lazy" />
              <img src="/founder/columbus-destroyers.png" alt="Columbus Destroyers" className="h-9 md:h-10 object-contain" loading="lazy" />
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/40 border-l border-white/15 pl-4">
                + NFL Combine ·
                <br />National Football Scouting
              </div>
            </div>

            <div className="mt-8">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-sm tracking-[0.18em] font-bold uppercase text-electric hover:gap-3 transition-all"
                data-testid="founder-strip-cta"
              >
                Read His Story
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SCHOOLS MARQUEE — hidden until we have real verified scouting data.
          When VERIFIED has actual school-scout signals, re-enable by uncommenting
          the line below. Until then, advertising "Programs Already Scouting"
          with hardcoded fallback schools is misleading. */}
      {/* <SeenByMarquee /> */}

      {/* VERIFIED ATHLETE SHOWCASE — hidden until we have real verified athletes
          to feature. To bring back, drop the `&& false` guard below. */}
      {stats.showcase && stats.showcase.length > 0 && false && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-mono mb-3">Verified Athletes</div>
              <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
                The roster.
              </h2>
            </div>
            <Link to="/dashboard/recruiter" className="text-electric text-sm font-display font-bold uppercase tracking-wider hover:underline" data-testid="showcase-view-all">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {stats.showcase.slice(0, 4).map((a, i) => (
              <AthleteCard key={a.id} athlete={a} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* EVENTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-mono mb-3">Upcoming</div>
            <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
              VERIFIED Events
            </h2>
          </div>
          <Link to="/events" className="text-electric text-sm font-display font-bold uppercase tracking-wider hover:underline" data-testid="events-view-all">
            All events →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {(() => {
            // Filter out junk stub events (no name OR no date) so the Coming Soon
            // block shows until we have a real event to advertise.
            const realEvents = events.filter((e) => e?.name && e?.date);
            return realEvents.length === 0;
          })() ? (
            <div className="md:col-span-3 bento-card p-10 md:p-14 text-center border-amber-400/25 bg-amber-400/5" data-testid="events-coming-soon-home">
              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-amber-400/30 bg-amber-400/10 mb-4">
                <Megaphone size={11} className="text-amber-300" />
                <span className="text-[10px] font-display font-bold tracking-widest uppercase text-amber-300">Coming Soon</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
                Group Combine Events <span className="text-amber-300">are on the way.</span>
              </h3>
              <p className="text-sm text-white/55 mt-4 max-w-xl mx-auto leading-relaxed">
                We're locking in venues for the next wave of VERIFIED Combines. In the meantime, book a private testing session — verified measurables on your own schedule.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <Link to="/register?role=parent" className="btn-primary text-xs" data-testid="events-cs-register">
                  Get on the list <ArrowRight size={12} />
                </Link>
                <button
                  disabled
                  className="text-xs font-display font-bold uppercase tracking-wider px-3 py-2 rounded-md border border-white/10 text-white/30 cursor-not-allowed inline-flex items-center gap-1.5"
                  data-testid="events-cs-preview-disabled"
                  title="No events scheduled yet"
                >
                  Preview the calendar
                  <span className="text-[9px] font-mono uppercase tracking-widest text-white/30">· soon</span>
                </button>
              </div>
            </div>
          ) : (
            events.map((e, i) => (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                className={`bento-card overflow-hidden group animate-fade-up stagger-${i + 1}`}
                data-testid={`landing-event-${e.id}`}
              >
                {e.image_url && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <LazyImage
                      src={e.image_url}
                      alt={e.title}
                      aspect="aspect-[16/9]"
                      rounded=""
                      className="!rounded-none w-full h-full"
                      imgClass="opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700"
                    />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-electric font-display font-bold tracking-wider mb-2">
                    <Calendar size={12} />
                    {new Date(e.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight">{e.title}</h3>
                  <p className="text-sm text-white/50 mt-1">{e.facility} · {e.city}, {e.state}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-display text-lg font-black text-electric">${e.price}</span>
                    <span className="text-xs text-white/60 group-hover:text-electric transition-colors">Register →</span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* TRUST CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 p-10 md:p-16 bg-[#0a0a0a]">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 80% 50%, rgba(0,229,255,0.4), transparent 60%)" }} />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="label-mono mb-3">For Parents & Athletes</div>
              <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
                Built on <span className="text-electric">trust</span>.<br />
                Not hype.
              </h2>
              <p className="text-white/60 mt-6 max-w-md">
                We don't sell rankings. We don't promise scholarships. We deliver verified data and
                independent professional evaluations — so your athlete's story is the truth.
              </p>
              <div className="mt-8 flex gap-3 flex-wrap">
                <Link to="/register" className="btn-primary" data-testid="cta-register-btn">
                  Get VERIFIED <ChevronRight size={16} />
                </Link>
                <Link to="/events" className="btn-ghost" data-testid="cta-events-btn">
                  Browse Events
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TrustTile title="Verified Measurables" sub="Height · Weight · Wingspan · Arm · Hand" />
              <TrustTile title="Verified Testing" sub="40 · Shuttle · 3-Cone · Vert · Broad" />
              <TrustTile title="NFL-style Reports" sub="1.0 – 9.0 trait scale" />
              <TrustTile title="Recruiter-ready" sub="Searchable · Sortable · Trusted" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatTile({ label, value, suffix = "", comingSoon = false }) {
  if (comingSoon) {
    return (
      <div className="stat-tile" data-testid={`stat-${label.toLowerCase().replace(/\s|\(|\)/g, "-")}-coming-soon`}>
        <div className="label-mono">{label}</div>
        <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded border border-amber-400/30 bg-amber-400/10">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
          <span className="text-[10px] font-display font-bold tracking-widest uppercase text-amber-300">Coming Soon</span>
        </div>
        <div className="font-display text-3xl md:text-4xl font-black text-white/30 mt-3 leading-none">—</div>
      </div>
    );
  }
  return (
    <div className="stat-tile">
      <div className="label-mono">{label}</div>
      <div className="font-display text-4xl md:text-5xl font-black text-white mt-1">
        {value}
        <span className="text-electric">{suffix}</span>
      </div>
    </div>
  );
}

function BentoCard({ className = "", icon, eyebrow, title, description, image }) {
  return (
    <div className={`bento-card p-6 md:p-8 relative overflow-hidden ${className}`}>
      {image && (
        <div className="absolute inset-0 opacity-15">
          <img src={image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/80 to-transparent" />
        </div>
      )}
      <div className="relative flex flex-col h-full">
        <div className="flex items-center gap-2 text-electric mb-4">{icon}<span className="label-mono text-electric/80">{eyebrow}</span></div>
        <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight">{title}</h3>
        <p className="text-sm text-white/60 mt-3 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function TrustTile({ title, sub }) {
  return (
    <div className="bg-black/50 border border-white/10 rounded-lg p-4">
      <div className="font-display text-lg font-bold uppercase tracking-tight text-white">{title}</div>
      <div className="text-xs text-white/40 mt-1">{sub}</div>
    </div>
  );
}

function Step({ n, icon, title, body }) {
  return (
    <div className="bento-card p-6 relative overflow-hidden flex flex-col">
      <div className="absolute -top-4 -right-2 font-display text-7xl font-black text-white/[0.03] pointer-events-none select-none">
        {n}
      </div>
      <div className="flex items-center gap-2 text-electric mb-4">
        {icon}
        <span className="label-mono text-electric/80">Step {n}</span>
      </div>
      <h3 className="font-display text-xl font-bold uppercase tracking-tight leading-tight">{title}</h3>
      <p className="text-sm text-white/60 mt-3 leading-relaxed">{body}</p>
    </div>
  );
}
